import { useEffect, useRef } from 'react'
import { KaraokeSpan } from '../utils/karaoke'
import { getStoryAudio, storyAudioActiveWithin, KARAOKE_IDLE_MS } from '../utils/storyAudio'

/**
 * Drives the follow-along highlighting while the teacher recording plays.
 *
 * Renders into a document-level overlay (pointer-events: none):
 *  - one continuous band per text line covering the sentence being read (a
 *    single block — spaces and inter-word gaps included), and
 *  - a "pill" on the word being read that GLIDES from word to word (CSS
 *    transition) and stays visible through pauses.
 *
 * Both tints are opaque warm paper, so they must sit BEHIND the glyphs: the
 * effect adds `karaoke-lifted` to <body> for its lifetime, which raises every
 * word above the overlay (see App.css). The text therefore keeps its normal
 * colour and the mastery underlines stay visible.
 *
 * The rAF loop runs for the lifetime of the story (`timeupdate` fires ~4×/s,
 * far too coarse for word-level highlighting; an always-on loop also lets a
 * React re-render that replaces the word spans self-heal within a frame —
 * class toggles and measurements re-resolve from the stable DOM ids).
 *
 * The visuals only show while the learner is actually listening: playing, or
 * paused for less than KARAOKE_IDLE_MS. After that they clear, and a tap on a
 * word goes back to being a plain dictionary tap (see Story.tsx).
 */

const OVERLAY_ID = 'karaoke-overlay'
const LIFT_CLASS = 'karaoke-lifted'
const BAND_PADDING_X = 3
const BAND_PADDING_Y = 2
const LINE_CLUSTER_TOLERANCE = 6

interface Rect {
  left: number
  top: number
  right: number
  bottom: number
}

/** The character span inside a word span. */
const charEl = (id: string): HTMLElement | null => {
  const wordEl = document.getElementById(id)
  if (!wordEl) return null
  return (wordEl.querySelector('.main_title_big_text') as HTMLElement) || wordEl
}

/** Document-coordinate rect of the character span inside a word span. */
const charRect = (id: string): Rect | null => {
  const el = charEl(id)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (!r.width && !r.height) return null
  return {
    left: r.left + window.scrollX,
    top: r.top + window.scrollY,
    right: r.right + window.scrollX,
    bottom: r.bottom + window.scrollY,
  }
}

export function useKaraokeHighlight(
  audioUrl: string | undefined,
  timeline: KaraokeSpan[] | null,
  /** Called with the timeline sentence being read, or null when idle. */
  onSentenceChange?: (sentence: number | null) => void,
) {
  const onSentenceChangeRef = useRef(onSentenceChange)
  onSentenceChangeRef.current = onSentenceChange

  useEffect(() => {
    if (!audioUrl || !timeline || !timeline.length) return undefined

    const audio = getStoryAudio(audioUrl)
    const sentenceIds = new Map<number, string[]>()
    timeline.forEach((span) => {
      const ids = sentenceIds.get(span.sentence) || []
      ids.push(span.id)
      sentenceIds.set(span.sentence, ids)
    })

    const overlay = document.createElement('div')
    overlay.id = OVERLAY_ID
    // `mix-blend-mode: multiply` is what guarantees the tints stay BEHIND the
    // glyphs. The `karaoke-lifted` word lift below only works while nothing
    // between <body> and a word span creates a stacking context, and on narrow
    // viewports one does: maayot.css raises `.scroll_contain_div` to z-index 0
    // under 768px, which traps every lifted word inside it and lets this
    // overlay paint over the sentence being read (learner report, Sep 2026).
    // Multiplying the whole overlay group onto the page keeps the text
    // readable whatever the stacking order turns out to be: the tints are
    // near-white, so they darken the paper and leave the dark glyphs and the
    // mastery underlines showing through.
    overlay.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:0;pointer-events:none;z-index:5;mix-blend-mode:multiply;'
    const pill = document.createElement('div')
    pill.className = 'karaoke-pill'
    pill.style.opacity = '0'
    overlay.appendChild(pill)
    document.body.appendChild(overlay)
    document.body.classList.add(LIFT_CLASS)
    let bands: HTMLDivElement[] = []

    let frame = 0
    let pillPlaced = false
    let prevPillId: string | null = null
    let prevSentence: number | null = null

    const placeBands = (sentence: number | null) => {
      bands.forEach((band) => band.remove())
      bands = []
      if (sentence === null) return
      // Cluster the sentence's character rects into text lines, one band per
      // line spanning from the first to the last rect — spaces, punctuation
      // gaps and word margins are covered by the single block.
      const rects = (sentenceIds.get(sentence) || [])
        .map(charRect)
        .filter((r): r is Rect => !!r)
        .sort((a, b) => a.top - b.top)
      let line: Rect | null = null
      const lines: Rect[] = []
      rects.forEach((r) => {
        if (line && Math.abs(r.top - line.top) <= LINE_CLUSTER_TOLERANCE) {
          line.left = Math.min(line.left, r.left)
          line.right = Math.max(line.right, r.right)
          line.bottom = Math.max(line.bottom, r.bottom)
        } else {
          line = { ...r }
          lines.push(line)
        }
      })
      lines.forEach((l) => {
        const band = document.createElement('div')
        band.className = 'karaoke-band'
        band.style.left = `${l.left - BAND_PADDING_X}px`
        band.style.top = `${l.top - BAND_PADDING_Y}px`
        band.style.width = `${l.right - l.left + 2 * BAND_PADDING_X}px`
        band.style.height = `${l.bottom - l.top + 2 * BAND_PADDING_Y}px`
        overlay.insertBefore(band, pill)
        bands.push(band)
      })
    }

    // The word the pill is currently on, kept so a React re-render that
    // replaces the word spans can be detected and re-measured.
    let activeCharEl: HTMLElement | null = null

    const placePill = (id: string | null) => {
      activeCharEl = id ? charEl(id) : null
      if (!id) {
        pill.style.opacity = '0'
        pillPlaced = false
        return
      }
      const r = charRect(id)
      if (!r) return
      // First placement after being hidden must not glide in from (0,0).
      if (!pillPlaced) {
        pill.style.transition = 'none'
      }
      pill.style.left = `${r.left - BAND_PADDING_X}px`
      pill.style.top = `${r.top - BAND_PADDING_Y}px`
      pill.style.width = `${r.right - r.left + 2 * BAND_PADDING_X}px`
      pill.style.height = `${r.bottom - r.top + 2 * BAND_PADDING_Y}px`
      pill.style.opacity = '1'
      if (!pillPlaced) {
        void pill.offsetWidth // flush so the next move transitions again
        pill.style.transition = ''
        pillPlaced = true
      }
    }

    const clear = () => {
      placeBands(null)
      placePill(null)
      prevPillId = null
      if (prevSentence !== null) onSentenceChangeRef.current?.(null)
      prevSentence = null
    }

    const tick = () => {
      // Only highlight while the learner is listening (playing, or paused
      // briefly). Idle or finished: clear and go back to plain reading.
      if (audio.ended || !storyAudioActiveWithin(KARAOKE_IDLE_MS)) {
        if (prevPillId !== null || prevSentence !== null) clear()
        return
      }

      const timeMs = audio.currentTime * 1000

      // Last span that has started: drives the sentence band (so it persists
      // through mid-sentence pauses) and anchors the pill.
      let lo = 0
      let hi = timeline.length - 1
      let anchor = -1
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        if (timeline[mid].startMs <= timeMs) {
          anchor = mid
          lo = mid + 1
        } else {
          hi = mid - 1
        }
      }

      // The pill parks on the most recent WORD (skipping punctuation) and
      // stays there until the next word starts — always visible, no blinking.
      let pillIndex = anchor
      while (pillIndex >= 0 && !timeline[pillIndex].isWord) pillIndex--
      const pillId = pillIndex >= 0 ? timeline[pillIndex].id : null
      const currentSentence = anchor >= 0 ? timeline[anchor].sentence : null

      const sentenceChanged = currentSentence !== prevSentence
      const pillChanged = pillId !== prevPillId
      // Bands are re-measured at word cadence too, so font-size changes and
      // reflows self-heal quickly (identical rects produce no visual change).
      if (sentenceChanged || pillChanged) {
        placeBands(currentSentence)
        placePill(pillId)
        if (sentenceChanged) onSentenceChangeRef.current?.(currentSentence)
        prevSentence = currentSentence
        prevPillId = pillId
      } else if (activeCharEl && !activeCharEl.isConnected) {
        // A React re-render replaced the word spans mid-word (e.g. a
        // dictionary tap): re-resolve and re-measure at the current position.
        placeBands(currentSentence)
        placePill(pillId)
      }
    }

    const loop = () => {
      tick()
      frame = requestAnimationFrame(loop)
    }
    const remeasure = () => {
      // Layout moved under the overlay (resize / font change): re-place at
      // the current position.
      placeBands(prevSentence)
      if (prevPillId) placePill(prevPillId)
    }

    window.addEventListener('resize', remeasure)
    frame = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', remeasure)
      document.body.classList.remove(LIFT_CLASS)
      overlay.remove()
      onSentenceChangeRef.current?.(null)
    }
  }, [audioUrl, timeline])
}
